import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import http from '@/libs/http'
import { useBaseStore } from './base.store'
export interface IComapp {
	_id: string
	locationId: string
	// other comapp-specific fields
	[key: string]: any
}

export const useComappStore = defineStore('comapp', () => {
	// --- STATE ---
	const comapps = ref<IComapp[]>([])
	const comappsStorage = ref<any[]>([])
	const loadingMap = ref<Record<string, boolean>>({})
	const errorMap = ref<Record<string, string>>({})
	let listComappsPromise: Promise<IComapp[]> | null = null

	async function withLoading<T>(key: string, fn: () => Promise<T>): Promise<T> {
		if (loadingMap.value[key]) {
			throw new Error(`🟧 ${key} is already in progress (comappStore)`)
		}
		try {
			loadingMap.value[key] = true
			return await fn()
		} catch (e: any) {
			// Don't log errors for aborted requests
			if (e.name === 'AbortError') {
				console.warn(`🟨 ${key} was aborted (comappStore)`)
				throw e
			}
			console.error(`🟥 Error in ${key} (comappStore):`, e)
			errorMap.value[key] = e.message || 'Unknown error'
			throw e
		} finally {
			delete loadingMap.value[key]
		}
	}

	// --- GETTERS ---

	const comappsByLId = computed(() => {
		// map comapps by locationId
		const map: Record<string, IComapp[]> = {}
		comapps.value.forEach((comapp) => {
			if (!map[comapp.locationId]) {
				map[comapp.locationId] = []
			}
			map[comapp.locationId].push(comapp)
		})
		return map
	})

	const listComapps = async () => {
		if (listComappsPromise) return listComappsPromise
		listComappsPromise = withLoading('listComapps', async () => {
			const resp = await http.getAuth<{
				data: IComapp[]
				storage: any[]
			}>('/api/v2/comapps')
			comapps.value = resp.data || []
			comappsStorage.value = resp.storage || []
			return comapps.value
		})
		listComappsPromise.finally(() => {
			listComappsPromise = null
		})
		return listComappsPromise
	}

	const updateComapp = async (data: IComapp) =>
		withLoading(`updateComapp-${data._id || data.pcName || 'comapp'}`, async () => {
			const resp = await http.postAuth<any>(`/api/v1/comapp/update`, data, {
				headers: { hubids: (data as any).hubids },
			})
			await listComapps()
			try {
				const baseStore = useBaseStore()
				await baseStore.fetchStates()
			} catch (error) {
				console.warn('Failed to refresh states after comapp update', error)
			}
			return resp
		})

	const listBinGraphKeys = async (filepath: string) =>
		withLoading(`listBinGraphKeys-${filepath}`, async () => {
			const resp = await http.getAuth<any>(`/api/v1/bingraphkeys/${filepath}`)
			return resp
		})

	const listBinGraphData = async (data: { base64path: string; key: string }) =>
		withLoading(`listBinGraphData-${data.base64path}-${data.key}`, async () => {
			const resp = await http.getAuth<any>(`/api/v1/bingraph/${data.base64path}/${data.key}`)
			return resp
		})

	const binChartData = async (data: {
		pcName: string
		binKey: string
		yearFrom: string
		monthFrom: string
		yearTo: string
		monthTo: string
	}) =>
		withLoading(
			`binChartData-${data.pcName}-${data.yearFrom}-${data.monthFrom}-${data.yearTo}-${data.monthTo}`,
			async () => {
				const resp = await http.getAuth<any>(
					`/api/v1/binchartdata/${data.pcName}/${data.binKey}/${data.yearFrom}/${data.monthFrom}/${data.yearTo}/${data.monthTo}`,
				)
				return resp
			},
		)

	const createCommappZip = async (data: any) =>
		// data.pcName-data.year-data.month-data.part
		withLoading(
			`createCommappZip-${data.pcName || ''}-${data.year || ''}-${data.month || ''}-${data.part || ''}`,
			async () => {
				console.log('createCommappZip', { data })
				const resp = await http.postAuth<any>(`/api/v1/admin/createcommappzip`, data)
				return resp
			},
		)

	// listComapps()

	return {
		comapps,
		loadingMap,

		comappsByLId,
		createCommappZip,
		listComapps,
		updateComapp,
		listBinGraphKeys,
		listBinGraphData,
		binChartData,
	}
})
